import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import TransactionActionsMenu from './TransactionActionsMenu';

const RecentTransactionsAgent = ({ 
  onSendMoney, 
  onViewDetails, 
  onContactCustomer, 
  onFlagTransaction, 
  onProcessRefund 
}) => {
  const [transactions, setTransactions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [selectedFilter, setSelectedFilter] = useState('all');

  // Mock transaction data for agent
  const mockTransactions = [
    {
      id: 'TXN-789012',
      date: '2024-11-15',
      time: '14:32',
      customer: 'Sarah Johnson',
      fromCountry: 'US',
      toCountry: 'PH',
      recipient: 'Maria Santos',
      sendAmount: 1250.00,
      receiveAmount: 71875.00,
      sendCurrency: 'USD',
      receiveCurrency: 'PHP',
      exchangeRate: 57.5,
      commission: 43.75,
      commissionRate: 3.5,
      status: 'completed',
      method: 'bank_transfer'
    },
    {
      id: 'TXN-789011',
      date: '2024-11-14',
      time: '16:45',
      customer: 'David Chen',
      fromCountry: 'US',
      toCountry: 'CN',
      recipient: 'Wei Chen',
      sendAmount: 875.50,
      receiveAmount: 6234.56,
      sendCurrency: 'USD',
      receiveCurrency: 'CNY',
      exchangeRate: 7.12,
      commission: 30.64,
      commissionRate: 3.5,
      status: 'completed',
      method: 'cash_pickup'
    },
    {
      id: 'TXN-789010',
      date: '2024-11-14',
      time: '11:20',
      customer: 'Maria Garcia',
      fromCountry: 'US',
      toCountry: 'MX',
      recipient: 'Carlos Garcia',
      sendAmount: 2100.00,
      receiveAmount: 36750.00,
      sendCurrency: 'USD',
      receiveCurrency: 'MXN',
      exchangeRate: 17.5,
      commission: 84.00,
      commissionRate: 4.0,
      status: 'pending',
      method: 'bank_transfer'
    },
    {
      id: 'TXN-789009',
      date: '2024-11-13',
      time: '09:15',
      customer: 'James Wilson',
      fromCountry: 'US',
      toCountry: 'IN',
      recipient: 'Raj Patel',
      sendAmount: 650.75,
      receiveAmount: 54312.50,
      sendCurrency: 'USD',
      receiveCurrency: 'INR',
      exchangeRate: 83.45,
      commission: 22.78,
      commissionRate: 3.5,
      status: 'completed',
      method: 'mobile_wallet'
    },
    {
      id: 'TXN-789008',
      date: '2024-11-12',
      time: '15:30',
      customer: 'Anna Kowalski',
      fromCountry: 'US',
      toCountry: 'PL',
      recipient: 'Piotr Kowalski',
      sendAmount: 1800.00,
      receiveAmount: 7380.00,
      sendCurrency: 'USD',
      receiveCurrency: 'PLN',
      exchangeRate: 4.1,
      commission: 72.00,
      commissionRate: 4.0,
      status: 'failed',
      method: 'bank_transfer'
    }
  ];

  useEffect(() => {
    setIsLoading(true);
    // Simulate API call
    setTimeout(() => {
      setTransactions(mockTransactions);
      setIsLoading(false);
    }, 500);
  }, []);

  const filters = [
    { value: 'all', label: 'All Transactions' },
    { value: 'completed', label: 'Completed' },
    { value: 'pending', label: 'Pending' },
    { value: 'failed', label: 'Failed' }
  ];

  const filteredTransactions = transactions?.filter(transaction => {
    if (selectedFilter === 'all') return true;
    return transaction?.status === selectedFilter;
  });

  const getStatusBadge = (status) => {
    const badges = {
      completed: { text: 'Completed', className: 'bg-success/10 text-success', icon: 'CheckCircle' },
      pending: { text: 'Pending', className: 'bg-warning/10 text-warning', icon: 'Clock' },
      failed: { text: 'Failed', className: 'bg-error/10 text-error', icon: 'XCircle' },
      cancelled: { text: 'Cancelled', className: 'bg-muted text-muted-foreground', icon: 'X' }
    };
    return badges?.[status] || badges?.pending;
  };

  const getMethodIcon = (method) => {
    const methods = {
      bank_transfer: 'Building2',
      cash_pickup: 'MapPin',
      mobile_wallet: 'Smartphone',
      debit_card: 'CreditCard'
    };
    return methods?.[method] || 'ArrowRightLeft';
  };

  const getMethodLabel = (method) => {
    const methods = {
      bank_transfer: 'Bank Transfer',
      cash_pickup: 'Cash Pickup',
      mobile_wallet: 'Mobile Wallet',
      debit_card: 'Debit Card'
    };
    return methods?.[method] || method;
  };

  const getFlagEmoji = (countryCode) => {
    const flags = {
      'US': '🇺🇸',
      'PH': '🇵🇭',
      'CN': '🇨🇳',
      'MX': '🇲🇽',
      'IN': '🇮🇳',
      'PL': '🇵🇱',
      'GB': '🇬🇧',
      'DE': '🇩🇪'
    };
    return flags?.[countryCode] || '🌍';
  };

  const handleCloseActionsMenu = () => {
    setSelectedTransaction(null);
  };

  return (
    <div className="bg-card rounded-lg shadow-sm border border-border p-6">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-foreground">Transaction Activity</h3>
          <div className="flex items-center space-x-2">
            <select
              value={selectedFilter}
              onChange={(e) => setSelectedFilter(e?.target?.value)}
              className="text-sm border border-border rounded px-3 py-1 bg-background"
            >
              {filters?.map(filter => (
                <option key={filter?.value} value={filter?.value}>
                  {filter?.label}
                </option>
              ))}
            </select>
            <Button
              variant="outline"
              size="sm"
              iconName="ExternalLink"
              iconPosition="right"
              onClick={() => console.log('View detailed transaction history')}
            >
              View All
            </Button>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="text-center">
            <div className="text-xl font-bold text-foreground">
              {transactions?.filter(t => t?.status === 'completed')?.length}
            </div>
            <div className="text-sm text-muted-foreground">Completed Today</div>
          </div>
          
          <div className="text-center">
            <div className="text-xl font-bold text-warning">
              {transactions?.filter(t => t?.status === 'pending')?.length}
            </div>
            <div className="text-sm text-muted-foreground">Pending</div>
          </div>
          
          <div className="text-center">
            <div className="text-xl font-bold text-success">
              ${transactions?.reduce((sum, t) => sum + (t?.commission || 0), 0)?.toFixed(2)}
            </div>
            <div className="text-sm text-muted-foreground">Commission Earned</div>
          </div>
          
          <div className="text-center">
            <div className="text-xl font-bold text-primary">
              {Math.round((transactions?.filter(t => t?.status === 'completed')?.length / transactions?.length) * 100)}%
            </div>
            <div className="text-sm text-muted-foreground">Success Rate</div>
          </div>
        </div>

        {/* Transactions List */}
        <div className="space-y-3">
          {isLoading ? (
            <div className="text-center py-8">
              <Icon name="Spinner" size={32} className="text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">Loading transaction data...</p>
            </div>
          ) : filteredTransactions?.length === 0 ? (
            <div className="text-center py-8">
              <Icon name="ArrowRightLeft" size={48} className="text-muted-foreground mx-auto mb-4" />
              <h4 className="text-lg font-medium text-foreground mb-2">No transactions found</h4>
              <p className="text-muted-foreground">
                {selectedFilter === 'all' 
                  ? "Start processing transactions to see activity here."
                  : `No ${selectedFilter} transactions at this time.`
                }
              </p>
            </div>
          ) : (
            transactions?.map((transaction) => (
              <div
                key={transaction?.id}
                className="flex items-center justify-between p-4 bg-muted/50 rounded-lg hover:bg-muted transition-colors"
              >
                <div className="flex items-center justify-between flex-1">
                  {/* Left Section - Transaction Info */}
                  <div className="flex items-center space-x-4 flex-1">
                    {/* Method Icon */}
                    <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                      <Icon name={getMethodIcon(transaction?.method)} size={18} className="text-primary" />
                    </div>

                    {/* Transaction Details */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2 mb-1">
                        <span className="font-medium text-foreground">{transaction?.customer}</span>
                        <span className="text-xs font-mono text-muted-foreground">
                          {transaction?.id}
                        </span>
                        <span className={`px-2 py-1 text-xs rounded-full ${getStatusBadge(transaction?.status)?.className}`}>
                          {getStatusBadge(transaction?.status)?.text}
                        </span>
                      </div>
                      
                      <div className="flex items-center space-x-4 text-sm text-muted-foreground">
                        <div className="flex items-center space-x-1">
                          <span>{getFlagEmoji(transaction?.fromCountry)}</span>
                          <span>→</span>
                          <span>{getFlagEmoji(transaction?.toCountry)}</span>
                        </div>
                        
                        <span>{getMethodLabel(transaction?.method)}</span>
                        
                        <span>
                          {transaction?.date} at {transaction?.time}
                        </span>
                      </div>
                      
                      <div className="text-sm text-muted-foreground mt-1">
                        To: <span className="text-foreground">{transaction?.recipient}</span>
                      </div>
                    </div>
                  </div>

                  {/* Right Section - Amount & Commission */}
                  <div className="text-right space-y-1">
                    <div className="font-medium text-foreground">
                      {transaction?.sendCurrency} {transaction?.sendAmount?.toLocaleString()}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {transaction?.receiveCurrency} {transaction?.receiveAmount?.toLocaleString()}
                    </div>
                    <div className="text-sm font-medium text-success">
                      +${transaction?.commission?.toFixed(2)} ({transaction?.commissionRate}%)
                    </div>
                  </div>
                </div>

                <TransactionActionsMenu 
                  transaction={transaction}
                  onClose={handleCloseActionsMenu}
                  onViewDetails={onViewDetails}
                  onDownloadReceipt={(txn) => {
                    // Already handled in TransactionActionsMenu
                  }}
                  onPrintReceipt={(txn) => {
                    // Already handled in TransactionActionsMenu
                  }}
                  onContactCustomer={onContactCustomer}
                  onFlagTransaction={onFlagTransaction}
                  onProcessRefund={onProcessRefund}
                />
              </div>
            ))
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-border">
          <Button
            variant="default"
            size="sm"
            iconName="Send"
            iconPosition="left"
            onClick={onSendMoney}
            className="sm:flex-1"
          >
            Process New Transaction
          </Button>
          
          <Button
            variant="outline"
            size="sm"
            iconName="Download"
            iconPosition="left"
            onClick={() => console.log('Export transaction report')}
            className="sm:flex-1"
          >
            Export Report
          </Button>
        </div>
      </div>
    </div>
  );
};

export default RecentTransactionsAgent;